// Centralized planner-rules configuration layer.
//
// Field names mirror the Supabase `planner_rules` table so this local
// default can later be swapped in-place for the active DB row.

const DEFAULT_RULES = Object.freeze({
  version: 1,

  // Goal calorie deltas (percent of TDEE).
  lose_fat_delta_pct: -15,
  build_muscle_delta_pct: 10,
  maintain_delta_pct: 0,
  higher_protein_delta_pct: 0,
  lower_carb_delta_pct: 0,
  glp1_high_protein_delta_pct: 0,

  // TDEE activity multipliers.
  activity_multipliers: {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
  },

  // Scoring weights (used in Phase 2B).
  higher_protein_weight: 1.5,
  lower_carb_weight: 1.5,
  glp1_small_meal_weight: 2.0,
  preference_match_weight: 1.0,

  // Macro-preset configuration (all admin-tunable).
  // Automatic-mode target calorie shares per preset, plus a
  // weight-based protein floor (g/kg) that can override the
  // percentage target for heavier customers. Carbohydrate is never
  // an unbounded residual -- see deriveMacroTargets() in plannerCalc.js.
  macro_preset_allocations: {
    auto: { protein_pct: 30, fat_pct: 30, carb_pct: 40, protein_g_per_kg: 1.6 },
    higher_protein: { protein_pct: 35, fat_pct: 30, carb_pct: 35, protein_g_per_kg: 2.0 },
    lower_carb: { protein_pct: 35, fat_pct: 40, carb_pct: 25, protein_g_per_kg: 1.6 },
  },
  // Fat is never allowed to drop below this share of total calories,
  // even when the protein floor forces a reallocation.
  macro_fat_floor_pct: 20,
  macro_deviation_warning_pct: 15,
});

// Placeholder for later Supabase hydration.
export async function getActivePlannerRules() {
  return DEFAULT_RULES;
}

// Sync accessor — screens read the defaults synchronously today.
export function getPlannerRulesSync() {
  return DEFAULT_RULES;
}

// Small helper to look up the delta pct by goal id.
export function goalDeltaPct(rules, goal) {
  switch (goal) {
    case 'lose_fat': return rules.lose_fat_delta_pct;
    case 'build_muscle': return rules.build_muscle_delta_pct;
    case 'maintain': return rules.maintain_delta_pct;
    case 'higher_protein': return rules.higher_protein_delta_pct;
    case 'lower_carb': return rules.lower_carb_delta_pct;
    case 'glp1_high_protein': return rules.glp1_high_protein_delta_pct;
    default: return 0;
  }
}

export const ACTIVITY_LABELS = {
  sedentary: 'Sedentary — little or no exercise',
  light: 'Lightly active — 1–3 days / week',
  moderate: 'Moderately active — 3–5 days / week',
  active: 'Very active — 6–7 days / week',
  very_active: 'Extra active — physical job or 2× / day',
};

export const HEALTH_GOALS = [
  { id: 'lose_fat',          label: 'Lose Body Fat',              blurb: 'A moderate calorie deficit around your maintenance.' },
  { id: 'build_muscle',      label: 'Build Muscle',               blurb: 'A small surplus with protein-forward meals.' },
  { id: 'maintain',          label: 'Maintain Weight',            blurb: 'Meals tuned to your estimated maintenance calories.' },
  { id: 'higher_protein',    label: 'Higher Protein',             blurb: 'Prioritize higher-protein meals across the plan.' },
  { id: 'lower_carb',        label: 'Lower Carb',                 blurb: 'Prioritize meals with lower carbohydrate loads.' },
  { id: 'glp1_high_protein', label: 'GLP-1 High-Protein Support', blurb: 'Smaller, protein-forward meals sized for GLP-1 users. Not medical advice.' },
];
